/**
 * Custom Web Component for handling Saved Design Cards.
 * Manages selection, variant updates, Shopify section reloading, and design removal.
 */
class CustomDesignCard extends HTMLElement {
  static SELECTED_CLASS = "pm__selector-item--selected";
  static CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  constructor() {
    super();

    this.form = null;
    this.productModule = null;
    this.currentSummary = null;
    this._currentDesignId = null;
    this.currentVariants = [];
    this.designStorage = window.DesignStorage
      ? new window.DesignStorage()
      : null;
    this._handleClick = this._handleClick.bind(this);
  }

  connectedCallback() {
    this.productModule = this.closest("product-component");
    this.form = this.productModule?.querySelector("form");
    this.addEventListener("click", this._handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._handleClick);
  }

  /**
   * Master click handler routing to either selection or removal logic.
   * @param {Event} e
   * @private
   */
  async _handleClick(e) {
    const removeBtn = e.target.closest(".pm__selector-item--remove");

    if (removeBtn) {
      e.stopPropagation();
      await this._handleRemove(removeBtn);
      return;
    }

    // If it's not a remove action, handle standard card selection
    await this._handleSelect();
  }

  /**
   * Core selection logic: Updates UI instantly, syncs form, and fetches new Shopify data.
   * @private
   */
  async _handleSelect() {
    // Prevent redundant network requests if already selected
    if (this.classList.contains(CustomDesignCard.SELECTED_CLASS)) return;

    const href = this.dataset.href;
    const summary = this._getSummary();

    if (!href) {
      console.warn("CustomDesignCard: No URL found for this design card.");
      return;
    }

    // Fetch and update external Shopify sections via Rendering API
    await this._fireShopifyReload(href);

    this._selectVisually();
    this._setInputsFromSummary(summary);
    this._updateVariantSelect(summary);
    this._dispatchSelectedEvent(summary);
  }

  /**
   * Visually selects the current card and deselects siblings.
   * @private
   */
  _selectVisually() {
    const gallery = this.closest("saved-designs-gallery");

    if (gallery) {
      const selectedCards = gallery.querySelectorAll(
        `custom-design-card.${CustomDesignCard.SELECTED_CLASS}`,
      );
      selectedCards.forEach((card) => {
        if (card !== this)
          card.classList.remove(CustomDesignCard.SELECTED_CLASS);
      });
    }

    this.classList.add(CustomDesignCard.SELECTED_CLASS);
  }

  /**
   * Updates the `<variant-selects>` web component if it exists.
   * @param {Object} summary
   * @private
   */
  _updateVariantSelect(summary) {
    const variantSelect = this.productModule?.querySelector("variant-selects");
    if (!variantSelect) return;

    const selectedOptions =
      summary?.config?.find((item) => item.options)?.options || [];
    const canvasId = summary?.config?.find((item) => item.canvasId)?.canvasId || null;
    const mediaList = summary?.mediaList || [];
    const mediaUrl = summary?.mediaUrl || "";

    try {
      variantSelect.getVariantData(this.currentVariants);
      
      const productSwiper = this.productModule?.querySelector(".pm__main-swiper");
      variantSelect.productSwiper = productSwiper;
      variantSelect.populateSwiper(mediaList, [], [], mediaUrl);

      variantSelect.init();
      variantSelect.updateOptions(selectedOptions);
      variantSelect.updateMasterId();
      variantSelect.updateVariantInput();
      variantSelect.updateCanvases(canvasId);
      variantSelect.toggleInputs();
      variantSelect.updatePrices();
      variantSelect.toggleAddButton();
      variantSelect.updateStickyDetails();

    } catch (error) {
      console.error("CustomDesignCard: Error updating variant selects:", error);
    }
  }

  /**
   * Maps summary configuration to the actual HTML form inputs.
   * @param {Object} summary
   * @private
   */
  _setInputsFromSummary(summary) {
    if (!summary?.config) return;

    const scope = document;

    Object.values(summary.config).forEach((option) => {
      // Update visible radio/checkbox options
      const inputs = scope.querySelectorAll(
        `input[name*="${option.slotName}"][value="${option.value}"]`,
      );

      inputs?.forEach((input) => (input.checked = true));

      // Update hidden line item properties
      const properties = scope.querySelectorAll(
        `input[type="hidden"][name*="${option.groupOptionName}"]`,
      );

      if (properties.length === 0) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${option.groupOptionName}]`;
        input.value = option.value;
        this.form.appendChild(input);
      } else {
        properties?.forEach((input) => (input.value = option.value));
      }
    });

    this._updateImageInputs(summary.imageUrl);
  }

  /**
   * Updates associated custom image inputs globally.
   * @param {String} imageUrl
   * @private
   */
  _updateImageInputs(imageUrl = "") {
    const imageInputs = document.querySelectorAll(
      "[data-custom-dyo-image-src]",
    );
    imageInputs?.forEach((input) => (input.value = imageUrl));
  }

  /**
   * Fetches updated HTML from Shopify's Section Rendering API.
   * Utilizes a static Promise cache keyed by the unique Design ID.
   * @param {String} url
   * @private
   */
  async _fireShopifyReload(url) {
    try {
      const sectionId = this.productModule?.id || "main-product";
      const designId = this.dataset.designId;
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set("section_id", sectionId);
      let html;

      // Use DesignStorage for persistent caching
      if (this.designStorage && designId) {
        const cachedEntry = await this.designStorage.getSectionCache(designId);
        const isCacheValid =
          cachedEntry &&
          Date.now() - cachedEntry.createdAt < CustomDesignCard.CACHE_TTL_MS;
        if (isCacheValid) {
          html = cachedEntry.html;
        } else {
          if (cachedEntry) {
            await this.designStorage.clearSectionCache(designId);
          }
          const response = await fetch(fetchUrl.toString());
          if (!response.ok)
            throw new Error(`HTTP error! status: ${response.status}`);
          html = await response.text();
          await this.designStorage.setSectionCache(designId, html);
        }
      } else {
        // Fallback to no cache if DesignStorage is not available
        const response = await fetch(fetchUrl.toString());
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        html = await response.text();
      }

      // Parse and replace DOM elements
      const newDoc = new DOMParser().parseFromString(html, "text/html");
      const selectorsToUpdate = [
        ".pm__main-swiper",
        ".pm__connected-items",
        'input[name="id"]',
        "#js-product-variants",
        '.standard-variant-selector'
      ];

      const oldElementScope = this.productModule || document;

      selectorsToUpdate.forEach((selector) => {
        const newEl = newDoc.querySelector(selector);
        const oldEl = oldElementScope.querySelector(selector);

        if (!newEl || !oldEl) return;
        
        if (selector === "#js-product-variants") {
          this.currentVariants = JSON.parse(newEl.textContent || "[]");
        }
        oldEl.outerHTML = newEl.outerHTML;
      });
    } catch (error) {
      console.error(
        "CustomDesignCard: Failed to reload Shopify sections",
        error,
      );
    }
  }

  /**
   * Handles deleting a saved design from IndexedDB/Storage and removing the DOM element.
   * @param {HTMLElement} removeBtn
   * @private
   */
  async _handleRemove(removeBtn) {
    const parent = removeBtn.closest(".pm__selector-item");
    if (!parent) return;

    const designId = parent.dataset.designId;
    const productId = parent.dataset.productId || this.dataset.productId;

    if (!designId || !productId || !this.designStorage) return;

    try {
      // 1. Delete the design data itself
      await this.designStorage.delete(productId, designId);

      // 2. Clear the associated section cache
      await this.designStorage.clearSectionCache(designId);

      parent.remove();

      // Clear state if the deleted design was currently active
      if (this._currentDesignId === designId) {
        this._currentDesignId = null;
      }
    } catch (error) {
      console.error(
        `CustomDesignCard: Failed to delete design ${designId} from storage:`,
        error,
      );
    }
  }

  /**
   * Parses and securely caches the JSON summary attached to the DOM element.
   * @returns {Object} Parsed summary object.
   * @private
   */
  _getSummary() {
    if (this.currentSummary) return this.currentSummary;

    try {
      this.currentSummary = JSON.parse(this.dataset.summary || "{}");
    } catch (error) {
      console.error(
        "CustomDesignCard: Failed to parse data-summary JSON",
        error,
      );
      this.currentSummary = {};
    }
    return this.currentSummary;
  }

  /**
   * Dispatches the event allowing external scripts to react to the card selection.
   * @param {Object} summary
   * @private
   */
  _dispatchSelectedEvent(summary) {
    if (!summary || Object.keys(summary).length === 0) return;

    this.dispatchEvent(
      new CustomEvent("dyo-design:selected", {
        detail: { summary },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

if (!customElements.get("custom-design-card")) {
  customElements.define("custom-design-card", CustomDesignCard);
}
